import { createContext, use, useCallback, useRef, useEffect } from 'react';
import { announceConfirmHydraTransition, subscribeToHydraTransitionAttempt } from '@customerio/hydra-nav-state';
import { useModals } from '@customerio/pluma-components/react';
import type { PlumaManagedModalId } from '@customerio/pluma-components/react';
import { ConfirmLeaveModal } from './confirm-leave-modal';

export type NavigationGuardOptions = {
  /**
   * Whether there are unsaved changes to protect.
   *
   * Accepts either a boolean (re-evaluated on each render) or a getter
   * function that the guard calls **synchronously** when a transition
   * is attempted. The function form lets callers read fresh state
   * (e.g. "just successfully saved, now navigating away") without
   * waiting for a re-render — useful when the navigation that should
   * skip the guard is queued in the same tick that flips the dirty
   * flag.
   */
  hasUnsavedChanges: boolean | (() => boolean);

  /**
   * Custom message to show in the confirmation modal
   */
  message?: string;

  /**
   * Callback to save changes before navigating away
   * If provided, a "Save and Continue" option will be shown
   */
  onSave?: () => Promise<void> | void;

  /**
   * Show "Save and Continue" disabled rather than hidden — for a form that has
   * a save action but cannot run it yet, e.g. while it fails validation. Pair
   * it with `message` so the prompt explains why.
   */
  isSaveDisabled?: boolean;

  /**
   * Callback to handle errors from the save operation
   */
  onError?: (error: unknown) => void;

  /**
   * Callback called when user chooses to discard changes
   */
  onDiscard?: () => void;
};

/** Normalize the boolean-or-getter form to a boolean. */
function resolveHasUnsavedChanges(value: NavigationGuardOptions['hasUnsavedChanges']): boolean {
  return typeof value === 'function' ? value() : value;
}

const NavigationGuardContext = createContext<{
  registerGuard: (options: NavigationGuardOptions) => void;
  unregisterGuard: () => void;
} | null>(null);

export function useNavigationGuard(options: NavigationGuardOptions) {
  const context = use(NavigationGuardContext);

  if (context == null) {
    throw new Error('useNavigationGuard must be used within a NavigationGuardProvider');
  }

  const { hasUnsavedChanges, message, onSave, isSaveDisabled, onError, onDiscard } = options;
  // For the beforeunload + effect deps we need a concrete boolean.
  // Function callers get re-evaluated as their state changes naturally
  // (the component re-renders → this hook runs → resolved value flips).
  const resolvedHasUnsavedChanges = resolveHasUnsavedChanges(hasUnsavedChanges);

  // Browser-level protection for refresh/close.
  useEffect(() => {
    if (!resolvedHasUnsavedChanges) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };

    window.addEventListener('beforeunload', handleBeforeUnload, { capture: true });
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload, { capture: true });
    };
  }, [resolvedHasUnsavedChanges]);

  // Mirror the latest `hasUnsavedChanges` into a ref on every render so
  // the provider can read fresh state synchronously inside the hydra
  // transition listener — without waiting for `useEffect` to re-run.
  // This is what enables the function form to skip the modal on a
  // post-save navigation queued in the same tick.
  const hasUnsavedChangesRef = useRef(hasUnsavedChanges);
  hasUnsavedChangesRef.current = hasUnsavedChanges;

  useEffect(() => {
    context.registerGuard({
      message,
      onSave,
      isSaveDisabled,
      onError,
      onDiscard,
      // Always register as a getter so the provider has one shape to
      // call. Reads the latest value from the ref so post-render state
      // flips (e.g. a save just completed) are visible immediately.
      hasUnsavedChanges: () => resolveHasUnsavedChanges(hasUnsavedChangesRef.current),
    });

    return () => {
      context.unregisterGuard();
    };
  }, [context, message, onSave, isSaveDisabled, onError, onDiscard]);
}

type NavigationGuardProviderProps = { children?: React.ReactNode };

export function NavigationGuardProvider({ children }: NavigationGuardProviderProps) {
  const modals = useModals();
  const currentGuardRef = useRef<NavigationGuardOptions | null>(null);

  // Non-null while the confirm-leave modal is open. Used to ignore repeat
  // transition attempts instead of stacking a second modal.
  const openModalIdRef = useRef<PlumaManagedModalId<typeof ConfirmLeaveModal> | null>(null);

  // Set up the subscription once when the provider mounts
  useEffect(() => {
    return subscribeToHydraTransitionAttempt(() => {
      const currentGuard = currentGuardRef.current;

      // If no guard is registered or no unsaved changes, immediately confirm
      if (currentGuard == null || !resolveHasUnsavedChanges(currentGuard.hasUnsavedChanges)) {
        announceConfirmHydraTransition();
        return;
      }

      // If the modal is already open, don't show another one
      if (openModalIdRef.current != null) {
        return;
      }

      // Show the modal for unsaved changes
      const modalId = modals.open(
        ConfirmLeaveModal,
        {
          message: currentGuard.message,
          onSave: currentGuard.onSave,
          isSaveDisabled: currentGuard.isSaveDisabled,
          onError: currentGuard.onError,
          onDiscard: currentGuard.onDiscard,
        },
        {
          // Don't leave an orphaned modal behind if this provider unmounts
          // while the modal is open (matches the previous inline <Modal>,
          // which unmounted together with the provider).
          shouldCloseOnUnmount: true,
          // Resolve `waitForClose` as soon as the close starts instead of
          // after the close animation, so a confirmed navigation kicks off
          // immediately — matching the previous behavior where
          // `proceedWithNavigation()` ran on click.
          withImmediateCallbacks: true,
        },
      );
      openModalIdRef.current = modalId;

      void modals.waitForClose(modalId).then((result) => {
        openModalIdRef.current = null;

        // 'confirm' means the user chose to leave (discarded their changes,
        // or saved successfully). Anything else — "Cancel", Esc, overlay
        // click, or a failed save — cancels the navigation attempt.
        if (result.reason === 'confirm') {
          announceConfirmHydraTransition();
        }
      });
    });
  }, [modals]);

  const registerGuard = useCallback((options: NavigationGuardOptions) => {
    currentGuardRef.current = options;
  }, []);

  const unregisterGuard = useCallback(() => {
    currentGuardRef.current = null;
  }, []);

  return (
    <NavigationGuardContext.Provider value={{ registerGuard, unregisterGuard }}>
      {children}
    </NavigationGuardContext.Provider>
  );
}
